import { query, where } from "firebase/firestore";
import { t } from "i18next";
import { DataTableData, DataTableHeader } from "../../../../../components/deprecated/DataTable/DataTable";
import { CollectionUtilities } from "../../../../../utils/collectionUtilities";
import { Utilities } from "../../../../../utils/utilities";
import { CompanyCollectionData } from "../../../Company/Collections/CompanyCollectionData";
import { PropertyCollectionData } from "../../../Property/Collections/PropertyCollectionData";
import { PropertyData } from "../../../Property/PropertyData";
import { DataObjectField } from "../../DataObjectField/DataObjectField";
import { LocalData } from "../../LocalData";
import { CollectionItemDataHelper, GenericCollectionItemType, GenericCollectionType } from "./CollectionItemDataHelper";

export class GlobalCollectionItemDataHelper extends CollectionItemDataHelper {
  id = () => "collectionItems-" + this.collectionID;

  collectionID: string;
  constructor(collectionID: string) {
    super(undefined);
    this.collectionID = collectionID;
  }

  loadAllCollections = (): Promise<any> => {
    const promises: Promise<any>[] = [];
    for (const property of LocalData.currentUser.properties) {
      const col = property.collections.getData(this.collectionID);
      if (!col || !col.loaded) {
        console.log("loading collection", this.collectionID, "for property", property.id);
        promises.push(property.collections.loadDocumentWithPromise(this.collectionID));
      }
    }
    return Promise.all(promises).catch((err) => {
      console.error(err);
      return undefined;
    });
  };

  getCollections = (): GenericCollectionType[] => {
    const collections: PropertyCollectionData[] = [];
    for (const property of LocalData.currentUser.properties) {
      const col = property.collections.getData(this.collectionID);
      if (col) collections.push(col);
    }
    return collections;
  };

  allFields(): DataObjectField<any, any>[] {
    return this.getFieldsForCollection();
  }

  override displayName = () => {
    const collections = this.getCollections();
    if (collections.length === 0) return "";
    return collections[0].name.translate();
  };

  override areAllLoaded(): boolean {
    return this.getCollections().every((col) => col.loaded && col.customFields.loaded);
  }

  override async loadAll(): Promise<any> {
    try {
      await this.loadAllCollections();
    } catch (err) {
      console.error(err);
    }

    const allGlobalPromises: Promise<any>[] = [];
    for (const collection of this.getCollections()) {
      const promises: Promise<any>[] = [];
      if (collection instanceof PropertyCollectionData) {
        const property = collection?.parent instanceof PropertyData ? collection?.parent : undefined;
        const member = property?.members.getData(LocalData.currentUser.id);
        const memberRoles = member?.roles.map((role) => role.id);
        if (memberRoles?.length) {
          promises.push(
            new Promise((resolve) => {
              collection.customFields.loadWithFilter(
                (ref) => query(ref, where("readAccess.roles", "array-contains-any", memberRoles)),
                () => {
                  resolve(undefined);
                }
              );
            })
          );
        }
        promises.push(collection.loadAllItems());
      } else if (collection instanceof CompanyCollectionData) {
        promises.push(collection.customFields.loadWithPromise());
        promises.push(collection.items.loadWithPromise());
      }

      if (!collection.loaded) {
        promises.push(collection?.load());
      }
      allGlobalPromises.push(Promise.all(promises));
    }
    return Promise.all(allGlobalPromises);
  }

  override getCollection = (): GenericCollectionType | undefined => {
    const collections = this.getCollections();
    if (collections.length === 0) return undefined;
    return collections[0];
  };

  override getAllDatas(): GenericCollectionItemType[] {
    const collections = this.getCollections();
    const allDatas: GenericCollectionItemType[] = [];
    for (const collection of collections) {
      for (const item of collection.items.documents) {
        allDatas.push(item);
      }
    }
    return allDatas;
  }

  override getDataTableDatas(documents: GenericCollectionItemType[]): DataTableData[] {
    return documents.map((item) => {
      const property = item.parent?.parent instanceof PropertyData ? item.parent.parent : undefined;
      const collection = this.getCollection();
      const data: DataTableData = {
        key: item.parent?.parent?.id + "-" + item.parent?.id + "-" + item.id,
        cells: {
          name: item.name,
          property: property?.name ?? "",
          visibleFor: Utilities.getReadAccessString(item.readAccess),
        },
      };

      for (const fieldID of this.getAllDataTableHeaders()
        .map((header) => header.key)
        .filter((key) => !Object.keys(data.cells).includes(key))) {
        data.cells[fieldID] = CollectionUtilities.getTableData(item.fields[fieldID], collection?.customFields.getData(fieldID), item);
      }

      return data;
    });
  }

  override getAllDataTableHeaders(): DataTableHeader[] {
    const headers: DataTableHeader[] = [
      {
        key: "name",
        title: t("main.name"),
      },
      {
        key: "visibleFor",
        title: t("main.visibleFor"),
      },
    ];
    const collection = this.getCollection();
    if (collection?.parent instanceof PropertyData) {
      headers.push({
        key: "property",
        title: t("main.property"),
      });
      headers.push({
        key: "propertyFiles",
        title: t("main.propertyFiles"),
      });
    }
    for (const field of collection?.customFields.documents ?? []) {
      headers.push({
        key: field.id,
        title: field.name.translate(),
      });
    }
    return headers;
  }
}

